import React from "react";
import { Link } from "react-router-dom";
import { MdCheck } from "react-icons/md";
import useCollision from "./useCollision";

/*
 * Kuest dropdown menu (components/ui/dropdown-menu.tsx) without Base UI:
 * panel  rounded-md border bg-popover p-1 shadow-md, min-w-32, 4px offset,
 *        fade + zoom-in-95 + 2px slide on open, fade + zoom-out-95 on close;
 * item   rounded-sm px-2 py-1.5 text-sm, highlight bg-accent, muted size-4 icons.
 * Keyboard: Enter/Space/ArrowDown on the trigger opens and focuses the first item,
 * arrows/Home/End move, Esc closes and returns focus to the trigger, Tab or a click
 * outside closes.
 */

const MenuContext = React.createContext(null);

export const menuPanelClass =
  "rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md outline-none";

export const menuItemClass =
  "relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground focus:bg-accent focus:text-accent-foreground aria-disabled:pointer-events-none aria-disabled:opacity-50 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-']):not([class*='h-'])]:h-4 [&_svg:not([class*='size-']):not([class*='h-'])]:w-4 [&_svg:not([class*='text-'])]:text-muted-foreground";

const ITEM_SELECTOR = '[role="menuitem"]:not([aria-disabled="true"]),[role="menuitemcheckbox"]:not([aria-disabled="true"]),[role="menuitemradio"]:not([aria-disabled="true"])';

function useControllable(open, onOpenChange) {
  const [inner, setInner] = React.useState(false);
  const controlled = typeof open === "boolean";
  const value = controlled ? open : inner;
  const set = React.useCallback(
    (next) => {
      if (!controlled) setInner(next);
      if (onOpenChange) onOpenChange(next);
    },
    [controlled, onOpenChange],
  );
  return [value, set];
}

export function DropdownMenu({
  trigger,
  children,
  align = "end",
  sideOffset = 4,
  className = "",
  wrapperClassName = "",
  open: openProp,
  onOpenChange,
  hoverOpen = false,
  label,
}) {
  const [open, setOpen] = useControllable(openProp, onOpenChange);
  const [mounted, setMounted] = React.useState(open);
  const [closing, setClosing] = React.useState(false);
  const wrapperRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const focusFirstRef = React.useRef(false);
  const hoverTimer = React.useRef(null);
  const hoveredRef = React.useRef(false);
  const menuId = React.useId();
  useCollision(panelRef, mounted, align);

  // Mount/unmount with a short exit animation.
  React.useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
      return undefined;
    }
    if (!mounted) return undefined;
    setClosing(true);
    const id = setTimeout(() => {
      setMounted(false);
      setClosing(false);
    }, 120);
    return () => clearTimeout(id);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const items = React.useCallback(() => (panelRef.current ? Array.from(panelRef.current.querySelectorAll(ITEM_SELECTOR)) : []), []);

  React.useEffect(() => {
    if (!open || !mounted) return undefined;
    if (focusFirstRef.current) {
      focusFirstRef.current = false;
      const first = items()[0];
      if (first) first.focus();
    }
    function onDown(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open, mounted, items, setOpen]);

  React.useEffect(() => () => clearTimeout(hoverTimer.current), []);

  const close = React.useCallback(
    (returnFocus = true) => {
      setOpen(false);
      if (returnFocus && triggerRef.current) triggerRef.current.focus();
    },
    [setOpen],
  );

  function onTriggerKeyDown(event) {
    if (event.key === "ArrowDown" || ((event.key === "Enter" || event.key === " ") && !open)) {
      event.preventDefault();
      focusFirstRef.current = true;
      setOpen(true);
    } else if ((event.key === "Enter" || event.key === " ") && open) {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === "Escape" && open) {
      event.preventDefault();
      close();
    }
  }

  function onPanelKeyDown(event) {
    const list = items();
    const index = list.indexOf(document.activeElement);
    if (event.key === "ArrowDown") {
      event.preventDefault();
      (list[(index + 1) % list.length] || list[0])?.focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      (list[index <= 0 ? list.length - 1 : index - 1] || list[0])?.focus();
    } else if (event.key === "Home") {
      event.preventDefault();
      list[0]?.focus();
    } else if (event.key === "End") {
      event.preventDefault();
      list[list.length - 1]?.focus();
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (event.key === "Tab") {
      setOpen(false);
    }
  }

  const hoverHandlers = hoverOpen
    ? {
        onPointerEnter: (event) => {
          if (event.pointerType !== "mouse") return;
          hoveredRef.current = true;
          clearTimeout(hoverTimer.current);
          setOpen(true);
        },
        onPointerLeave: (event) => {
          if (event.pointerType !== "mouse") return;
          hoveredRef.current = false;
          clearTimeout(hoverTimer.current);
          hoverTimer.current = setTimeout(() => setOpen(false), 120);
        },
      }
    : {};

  const triggerProps = {
    ref: triggerRef,
    "aria-haspopup": "menu",
    "aria-expanded": open,
    "aria-controls": open ? menuId : undefined,
    "data-state": open ? "open" : "closed",
    onClick: () => {
      // With hover-open the pointer already opened it; a click should not snap it shut.
      if (hoverOpen && open && hoveredRef.current) return;
      setOpen(!open);
    },
    onKeyDown: onTriggerKeyDown,
  };

  return (
    <MenuContext.Provider value={{ close }}>
      <div ref={wrapperRef} className={`relative inline-flex ${wrapperClassName}`} {...hoverHandlers}>
        {trigger({ open, props: triggerProps })}
        {mounted && (
          <div
            ref={panelRef}
            id={menuId}
            role="menu"
            aria-label={label}
            tabIndex={-1}
            onKeyDown={onPanelKeyDown}
            data-state={closing ? "closed" : "open"}
            style={{ top: `calc(100% + ${sideOffset}px)` }}
            className={`wasaa-menu absolute z-[70] min-w-[8rem] max-w-[calc(100vw-2rem)] overflow-y-auto overflow-x-hidden ${
              align === "end" ? "right-0 origin-top-right" : "left-0 origin-top-left"
            } max-h-[min(70vh,32rem)] ${menuPanelClass} ${className}`}
          >
            {typeof children === "function" ? children({ close }) : children}
          </div>
        )}
      </div>
    </MenuContext.Provider>
  );
}

export function useMenu() {
  return React.useContext(MenuContext);
}

export function MenuItem({ onSelect, disabled, variant, className = "", keepOpen = false, children, ...rest }) {
  const menu = useMenu();
  const destructive = variant === "destructive";
  return (
    <button
      type="button"
      role="menuitem"
      tabIndex={-1}
      aria-disabled={disabled ? "true" : undefined}
      disabled={disabled}
      onClick={(event) => {
        if (disabled) return;
        if (onSelect) onSelect(event);
        if (!keepOpen && menu) menu.close(false);
      }}
      className={`${menuItemClass} ${
        destructive ? "text-destructive hover:bg-destructive/10 hover:text-destructive focus:bg-destructive/10 focus:text-destructive [&_svg]:!text-destructive" : ""
      } ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

/** A menu item that navigates: `to` for in-app routes, `href` for anything else. */
export function MenuLinkItem({ to, href, className = "", children, onSelect, ...rest }) {
  const menu = useMenu();
  const shared = {
    role: "menuitem",
    tabIndex: -1,
    className: `${menuItemClass} ${className}`,
    onClick: (event) => {
      if (onSelect) onSelect(event);
      if (menu) menu.close(false);
    },
    ...rest,
  };
  if (to) {
    return (
      <Link to={to} {...shared}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} {...shared}>
      {children}
    </a>
  );
}

export function MenuCheckboxItem({ checked, onCheckedChange, disabled, className = "", children }) {
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={Boolean(checked)}
      tabIndex={-1}
      disabled={disabled}
      aria-disabled={disabled ? "true" : undefined}
      onClick={() => onCheckedChange && onCheckedChange(!checked)}
      className={`${menuItemClass} pl-8 ${className}`}
    >
      <span className="pointer-events-none absolute left-2 flex h-3.5 w-3.5 items-center justify-center" aria-hidden="true">
        {checked && <MdCheck className="h-4 w-4 !text-foreground" />}
      </span>
      {children}
    </button>
  );
}

export function MenuLabel({ className = "", children }) {
  return <div className={`px-2 py-1.5 text-sm font-medium ${className}`}>{children}</div>;
}

export function MenuSeparator({ className = "" }) {
  return <div role="separator" className={`-mx-1 my-1 h-px bg-border ${className}`} />;
}

export function MenuShortcut({ className = "", children }) {
  return <span className={`ml-auto text-xs tracking-widest text-muted-foreground ${className}`}>{children}</span>;
}

export default DropdownMenu;
